// ld-article-toc — in-article section navigation as a horizontal progress bar.
//
// Builds one link per <h2> in the article body, smooth-scrolls on click, and
// underlines the section currently in view via IntersectionObserver. When the
// links overflow the bar the row scrolls, and the active link is kept centred.
//
// Renders nothing at all when the article has no <h2> — the <nav> stays hidden.

(() => {
  const init = () => {
    const nav = document.querySelector('[data-ld-article-toc]');
    const list = document.querySelector('[data-ld-article-toc-list]');
    const scroller = document.querySelector('[data-ld-article-toc-scroller]');
    const body =
      document.querySelector('.blog-post-content.rte') ||
      document.querySelector('.main-blog-post__body .rte');
    if (!nav || !list || !body) return;

    const headings = Array.from(body.querySelectorAll('h2')).filter(
      (h) => h.textContent && h.textContent.trim().length > 0
    );

    // No sections to navigate: leave the nav hidden so it occupies no space.
    if (headings.length === 0) return;

    const slugify = (text) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const scrollBehavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

    // Land the heading just below the sticky bar.
    //
    // CSS `scroll-margin-top` cannot do this on its own: it resolves against
    // --ld-header-height, and LD's header collapses *while the smooth scroll is
    // running*, so the value the browser used at the start no longer matches
    // where the bar ends up. Measured: a jump landed 36px underneath the bar.
    // So once the scroll has settled, measure for real and correct.
    const settle = (heading) => {
      const barBottom = nav.getBoundingClientRect().bottom;
      const delta = heading.getBoundingClientRect().top - (barBottom + 16);
      if (Math.abs(delta) > 2) window.scrollBy({ top: delta, behavior: 'auto' });
    };

    // A click-jump also scrolls past every intervening heading, which would make
    // the observer fire repeatedly and flicker the underline. While a
    // click-driven scroll is in flight the observer defers to the clicked target.
    let lockedTo = null;
    let lockTimer = null;
    const release = () => {
      if (!lockedTo) return;
      const heading = lockedTo;
      lockedTo = null;
      clearTimeout(lockTimer);
      settle(heading);
    };
    const lock = (heading) => {
      lockedTo = heading;
      clearTimeout(lockTimer);
      // `scrollend` is not universal; fall back to a timeout of similar length.
      lockTimer = setTimeout(release, 900);
    };
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', release);
    }

    const items = headings.map((heading, idx) => {
      const text = heading.textContent.trim();
      const baseSlug = slugify(text) || `section-${idx + 1}`;
      let slug = baseSlug;
      let suffix = 2;
      while (document.getElementById(slug) && document.getElementById(slug) !== heading) {
        slug = `${baseSlug}-${suffix++}`;
      }
      heading.id = slug;

      const li = document.createElement('li');
      li.className = 'ld-article-toc__item';

      const a = document.createElement('a');
      a.className = 'ld-article-toc__link';
      a.href = `#${slug}`;
      a.textContent = text;
      a.addEventListener('click', (event) => {
        event.preventDefault();
        lock(heading);
        setActive(heading);
        heading.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        history.replaceState(null, '', `#${slug}`);
      });

      li.appendChild(a);
      list.appendChild(li);
      return { heading, item: li, link: a };
    });

    // Keep the active link visible when the row is scrollable. `nearest` would
    // not move a link that is already partly visible, so centre it instead —
    // but only when the row actually overflows, otherwise this scrolls the page.
    const revealActive = (link) => {
      if (!scroller) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      const target = link.offsetLeft - (scroller.clientWidth - link.offsetWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, target), behavior: scrollBehavior() });
    };

    let current = null;
    function setActive(heading) {
      if (heading === current) return;
      current = heading;
      items.forEach(({ heading: h, item, link }) => {
        const isActive = h === heading;
        item.classList.toggle('ld-article-toc__item--active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
          revealActive(link);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (lockedTo) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target);
      },
      // Top inset clears the sticky header + bar so a heading counts as "current"
      // only once it has passed under them. Bottom inset stops the last sections
      // all qualifying at once near the end of the page.
      { rootMargin: '-140px 0px -65% 0px', threshold: 0 }
    );

    items.forEach(({ heading }) => observer.observe(heading));

    // Fade the edges that have content beyond them, so an overflowing row reads
    // as scrollable. Nothing is faded when everything fits.
    const updateEdges = () => {
      if (!scroller) return;
      const overflowing = scroller.scrollWidth - scroller.clientWidth > 1;
      const atStart = scroller.scrollLeft <= 1;
      const atEnd = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 1;
      nav.classList.toggle('ld-article-toc--fade-start', overflowing && !atStart);
      nav.classList.toggle('ld-article-toc--fade-end', overflowing && !atEnd);
    };

    if (scroller) {
      scroller.addEventListener('scroll', updateEdges, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(updateEdges).observe(scroller);
    }

    // Before the first heading scrolls into range, show the first section.
    setActive(headings[0]);

    nav.hidden = false;
    updateEdges();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
